import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import { CreateLessonType } from './types'
import { ConnectModuleLessonType } from '../AdminLessonEditPage/types'

function* fetchModules(payload: { id: string }): SagaIterator {
  try {
    const modules = yield effects.call(api.fetchModules, payload.id)
    yield effects.put(actions.fetchModulesSuccess(modules.data))
  } catch (error) {
    console.warn('Cannot fetch modules', error)
    yield effects.put(actions.fetchModulesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchThemes(payload: { courseId: string }): SagaIterator {
  try {
    const themes = yield effects.call(api.fetchThemes, payload.courseId)
    yield effects.put(actions.fetchThemesSuccess(themes.data))
  } catch (error) {
    console.warn('Cannot fetch themes', error)
    yield effects.put(actions.fetchThemesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createLesson(payload: CreateLessonType): SagaIterator {
  try {
    const {
      id,
      mid,
      title,
      icon,
      theme,
      assistantCaption,
      indexNumber,
      timeToStudy,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position,
    } = payload
    const lesson = yield effects.call(api.createLesson, id, title, icon, theme)
    yield effects.put(
      actions.connectModuleLesson(
        mid,
        lesson.data?.lesson_id,
        indexNumber,
        timeToStudy,
        assistantCaption,
        isFirst,
        isLast,
        isFree,
        availableAt,
        position
      )
    )
  } catch (error) {
    console.warn('Cannot create lesson', error)
    yield effects.put(actions.createLessonFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* connectModuleLesson(payload: ConnectModuleLessonType): SagaIterator {
  try {
    const {
      mid,
      lid,
      indexNumber,
      timeToStudy,
      assistantCaption,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position,
    } = payload
    yield effects.call(
      api.connectModuleLesson,
      mid,
      lid,
      indexNumber,
      timeToStudy,
      assistantCaption,
      isFirst,
      isLast,
      isFree,
      availableAt,
      position
    )
    yield effects.put(actions.connectModuleLessonSuccess())
    yield effects.put(actions.createLessonSuccess())
  } catch (error) {
    console.warn('Cannot connect module with lesson', error)
    yield effects.put(
      actions.connectModuleLessonFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* watchFetchModules(): SagaIterator {
  yield effects.takeEvery(actions.fetchModules.toString(), fetchModules)
}

function* watchFetchThemes(): SagaIterator {
  yield effects.takeEvery(actions.fetchThemes.toString(), fetchThemes)
}

function* watchCreateLesson(): SagaIterator {
  yield effects.takeEvery(actions.createLesson.toString(), createLesson)
}

function* watchConnectModuleLesson(): SagaIterator {
  yield effects.takeEvery(
    actions.connectModuleLesson.toString(),
    connectModuleLesson
  )
}

export default function* watchAdminLessonCreate(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchThemes),
    effects.fork(watchCreateLesson),
    effects.fork(watchFetchModules),
    effects.fork(watchConnectModuleLesson),
  ])
}
